import {
  initializeAddressApiClient,
  initializeApiClient,
  initializeAppInsights,
  initializeSharedI18N,
  reportErrorToConsole,
  setupTracking,
  useFeatureFlags,
} from '@applyonline/shared'
import '@applyonline/shared/styles/themes/default.css'
import { DateFnsLocaleProvider } from '@if/core/date'
import i18n from '@if/core/i18n'
import { EFinancingProgram } from '@if/core/types'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { ReactQueryDevtools } from '@tanstack/react-query-devtools'
import { createRoot } from 'react-dom/client'
import { GoogleReCaptchaProvider } from 'react-google-recaptcha-v3'
import { I18nextProvider } from 'react-i18next'
import App from './containers/App/App'
import initializeGTM from './GTagManager'
import './index.css'
initializeApiClient(import.meta.env.PROD, import.meta.env.VITE_BACKEND_API_URL)
initializeAddressApiClient(import.meta.env.PROD, import.meta.env.VITE_BACKEND_ADDRESSAPI_URL)
const instrumentationKey = import.meta.env.VITE_APP_INSIGHT_INSTRUMENTAL_KEY
const ai = initializeAppInsights(instrumentationKey)
setupTracking(ai)
initializeGTM()

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: Infinity,
      refetchOnWindowFocus: false,
      retry: false,
    },
  },
})
const container = document.getElementById('root')
const root = createRoot(container!)

window.addEventListener('error', (event: ErrorEvent) => {
  if (event.message?.includes('Java object is gone')) {
    console.warn('Ignored WebView Java object error:', event.message);
    event.preventDefault(); // Prevent crash
  }
});

window.addEventListener('unhandledrejection', (event: PromiseRejectionEvent) => {
  const reason = event.reason as { message?: unknown };
  if (reason && typeof reason === 'object' && "message" in reason && typeof reason.message === 'string' && reason.message.includes('Java object is gone')) {
    console.warn('Ignored WebView Java object promise rejection:', event.reason);
    event.preventDefault();
  }
});

const AppWrapper = () => {
  const [flags] = useFeatureFlags(EFinancingProgram.B2c)

  const recaptchaUseEnterprise = flags?.useReCaptchaEnterpriseMigration2025 ?? true
  const recaptchaKey = import.meta.env.VITE_CAPTCHA_SITE_KEY || ''

  return (
    <GoogleReCaptchaProvider reCaptchaKey={recaptchaKey} useEnterprise={recaptchaUseEnterprise}>
      <I18nextProvider i18n={i18n}>
        <DateFnsLocaleProvider>
          <App />
          <div
            style={{
              fontSize: '16px',
            }}
          >
            <ReactQueryDevtools initialIsOpen={false} buttonPosition="top-right" />
          </div>
        </DateFnsLocaleProvider>
      </I18nextProvider>
    </GoogleReCaptchaProvider>
  )
}

initializeSharedI18N()
  .then(() => {
    root.render(
      <QueryClientProvider client={queryClient}>
        <AppWrapper />
      </QueryClientProvider>,
    )
  })
  .catch(reportErrorToConsole)
